import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, DollarSign, Users, TrendingUp, CheckCircle2, ArrowLeft } from 'lucide-react';
import MaintenanceBanner from './MaintenanceBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralPartnerSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    referrer_type: '', // real_estate_agent, individual, business
    company_name: '',
    country: '',
    how_heard: '',
    agreed_terms: false
  });

  const referrerTypes = [
    { value: 'real_estate_agent', label: 'Real Estate Agent' },
    { value: 'individual', label: 'Individual / Influencer' },
    { value: 'business', label: 'Business / Agency' },
    { value: 'property_professional', label: 'Property Professional' },
    { value: 'other', label: 'Other' }
  ];

  // Global country list organized by regions
  const globalCountries = [
    // Caribbean
    'Bahamas', 'Jamaica', 'Trinidad and Tobago', 'Barbados', 
    'Antigua and Barbuda', 'Saint Lucia', 'Grenada', 
    'Saint Vincent and the Grenadines', 'Dominica', 
    'Saint Kitts and Nevis', 'Belize', 'Guyana', 'Suriname',
    
    // North America
    'United States', 'Canada', 'Mexico',
    
    // Central & South America
    'Brazil', 'Argentina', 'Chile', 'Colombia', 'Peru', 'Venezuela',
    'Ecuador', 'Bolivia', 'Paraguay', 'Uruguay', 'Panama', 'Costa Rica',
    
    // Europe
    'United Kingdom', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands',
    'Belgium', 'Switzerland', 'Austria', 'Portugal', 'Greece', 'Ireland',
    'Sweden', 'Norway', 'Denmark', 'Finland', 'Poland',
    
    // Asia Pacific
    'Australia', 'New Zealand', 'Singapore', 'Hong Kong', 'Japan',
    'South Korea', 'India', 'Philippines', 'Malaysia', 'Thailand',
    
    // Middle East & Africa
    'United Arab Emirates', 'Saudi Arabia', 'South Africa', 'Nigeria',
    'Kenya', 'Egypt', 'Morocco',
    
    // Other
    'Other'
  ].sort();

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.referrer_type) {
      toast.error('Please select your type (Real Estate Agent, Individual, etc.)');
      return;
    }

    if (!formData.country) {
      toast.error('Please select your country');
      return;
    }

    if (!formData.agreed_terms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/referral/partner/apply`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        referrer_type: formData.referrer_type,
        company_name: formData.company_name,
        country: formData.country,
        how_heard: formData.how_heard
      });

      toast.success('Application Submitted!', {
        description: 'Check your email for your referral link and dashboard access.'
      });

      // Store token if provided
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user_role', 'referral_partner');
        localStorage.setItem('user_id', response.data.user_id);
      }

      setTimeout(() => {
        navigate('/referral/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Referral partner signup error:', error);
      toast.error('Application Failed', {
        description: error.response?.data?.detail || 'Please try again or contact support.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Building2 className="h-16 w-16 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Become a Referral Partner
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Earn 25% recurring commission by referring landlords, property managers, and HOA administrators to Estate Flow
          </p>
        </div>

        {/* Benefits Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="pt-6 text-center">
              <DollarSign className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">25% Commission</h3>
              <p className="text-sm text-gray-600">
                Earn recurring commission on all referred customer subscriptions
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="pt-6 text-center">
              <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Anyone Can Join</h3>
              <p className="text-sm text-gray-600">
                Real estate agents, influencers, businesses - everyone is welcome
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Track Performance</h3>
              <p className="text-sm text-gray-600">
                Real-time dashboard to monitor your referrals and earnings
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Application Form */}
        <Card className="max-w-2xl mx-auto bg-white/90 backdrop-blur shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl">Partner Application</CardTitle>
            <CardDescription>
              Complete the form below to start earning commissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Referrer Information */}
              <div className="space-y-2">
                <Label htmlFor="referrer_type">I am a *</Label>
                <Select
                  value={formData.referrer_type}
                  onValueChange={(value) => setFormData({...formData, referrer_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your type" />
                  </SelectTrigger>
                  <SelectContent>
                    {referrerTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">Company/Agency Name (Optional)</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  placeholder="Leave blank if individual"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData({...formData, country: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent>
                    {globalCountries.map(country => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Account Security */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-gray-500">Minimum 8 characters</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm Password *</Label>
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start space-x-2 pt-4">
                <Checkbox
                  id="agreed_terms"
                  checked={formData.agreed_terms}
                  onCheckedChange={(checked) => 
                    setFormData({...formData, agreed_terms: checked})
                  }
                />
                <label
                  htmlFor="agreed_terms"
                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I agree to the{' '}
                  <a href="/terms" className="text-emerald-600 hover:underline">
                    Referral Partner Terms
                  </a>{' '}
                  and understand I'll earn 25% recurring commission on referred subscriptions *
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                disabled={loading}
              >
                {loading ? (
                  'Submitting Application...'
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    Start Earning Commissions
                  </>
                )}
              </Button>

              <div className="text-center pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Already a Partner */}
        <div className="text-center mt-8">
          <p className="text-gray-600">
            Already a referral partner?{' '}
            <button
              onClick={() => navigate('/referral/login')}
              className="text-emerald-600 hover:underline font-medium"
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReferralPartnerSignup;
